import { AnyObject } from '@triare/auth-redux';
import React, { useEffect } from 'react';
import {
  CheckCircleOutlined, ExclamationCircleFilled, RedoOutlined, StopOutlined,
} from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import { ButtonProps } from 'antd/es/button/button';
import {
  useChangeUserStatus,
  UserTableRow,
  useUserDelete, useUserForgotPassword,
  useUserResetPassword,
  useUserSendInvitation,
} from '../../../hooks/api/users';
import { useSimpleModal } from '../../Common/Modal/Simple';
import { useAuth, User } from '../../../store/auth';
import { useMessageError, useMessageSuccess } from '../../../hooks/common';
import { listAction } from '../../Common/Table';
import { UserRole, UserStatusEnum } from '../../../enums/user';

interface ActionButtonProps<RequestRes = undefined> {
  record: UserTableRow; // Partial<UserTableRow>
  buttonProps?: ButtonProps;
  setTableKey?: (newVal: number) => void;
  afterSuccess?: (res?: RequestRes) => void;
}

export function DeleteButton({
  record, setTableKey, afterSuccess, buttonProps,
}: ActionButtonProps & AnyObject) {
  const userDelete = useUserDelete(undefined, 'admins');
  const { open, contextHolder } = useSimpleModal();
  const { user } = useAuth();

  useMessageError([userDelete]);
  useMessageSuccess([userDelete], 'User deleted successfully');

  useEffect(() => {
    if (!userDelete.error && !userDelete.loading && userDelete.data) {
      setTableKey?.(Date.now());
      afterSuccess?.();
    }
  }, [userDelete.error, userDelete.loading, userDelete.data]);

  const ActionButton = listAction.delete;

  if (user?.id === record.id) {
    return null;
  }

  return (
    <>
      <Tooltip title="Delete user">
        <ActionButton
          loading={userDelete.loading}
          title="Delete user"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            open({
              icon: <ExclamationCircleFilled />,
              title: 'Delete user?',
              content: (
                <span>
                  Are you sure you want to delete user
                  {' '}
                  <b>{record.email}</b>
                  ?
                </span>
              ),
              cancelText: 'Cancel',
              okText: 'Delete',
              okButtonProps: {
                danger: true,
              },
              onOk: () => userDelete.fetch(record.id),
            });
          }}
          {...buttonProps}
        />
      </Tooltip>
      {contextHolder}
    </>
  );
}

export function ReloadButton({ record, afterSuccess, buttonProps }: ActionButtonProps<undefined>) {
  const administratorSendInvitation = useUserSendInvitation();
  const administratorResetPassword = useUserResetPassword();

  useMessageError([administratorSendInvitation, administratorResetPassword]);
  useMessageSuccess([administratorSendInvitation], 'Invitation sent successfully');
  useMessageSuccess([administratorResetPassword], 'Reset-password link sent successfully');

  useEffect(() => {
    if (
      (administratorResetPassword.data && !administratorResetPassword.loading && !administratorResetPassword.error)
      || (
        administratorSendInvitation.data && !administratorSendInvitation.loading && !administratorSendInvitation.error)
    ) {
      afterSuccess?.();
    }
  }, [
    administratorResetPassword.data, administratorResetPassword.loading, administratorResetPassword.error,
    administratorSendInvitation.data, administratorSendInvitation.loading, administratorSendInvitation.error]);

  const ActionButton = listAction.reload;

  return (
    <ActionButton
      loading={administratorSendInvitation.loading}
      title="Reload administrator"
      onClick={(e: React.MouseEvent) => {
        e.preventDefault();

        return record.status === 'active' ? (
          administratorResetPassword.fetch(undefined, `${record.id}/reset-password`)
        ) : (
          administratorSendInvitation.fetch(undefined, `${record.id}/invitation`)
        );
      }}
      {...buttonProps}
    />
  );
}

export function DeactivateButton({
  record, setTableKey, afterSuccess, buttonProps,
}: ActionButtonProps<User>) {
  const { user } = useAuth();
  const { open, contextHolder } = useSimpleModal();
  const changeUserStatus = useChangeUserStatus(record.id);

  useMessageError([changeUserStatus]);
  useMessageSuccess([changeUserStatus], 'Action Successful!');
  // useMessageSuccess([administratorResetPassword], 'Reset-password link sent successfully');

  const isDeactivated = record.status === UserStatusEnum.DEACTIVATED;

  const deactivateUser = () => {
    changeUserStatus.fetch({ status: UserStatusEnum.DEACTIVATED });
  };

  const activateUser = () => {
    changeUserStatus.fetch({ status: UserStatusEnum.ACTIVE });
  };

  useEffect(() => {
    if (!changeUserStatus.error && !changeUserStatus.loading && changeUserStatus.data) {
      setTableKey?.(Date.now());
      afterSuccess?.(changeUserStatus.data);
    }
  }, [changeUserStatus.error, changeUserStatus.loading, changeUserStatus.data]);

  if (
    /** Disallow to deactivate yourself */
    user?.id === record.id
    /** Disallow for admin to deactivate other admins */
    || (user?.role === UserRole.ADMIN && record.role === UserRole.ADMIN)
  ) {
    return null;
  }

  return (
    <>
      <Tooltip title={`${isDeactivated ? 'Activate' : 'Deactivate'} user`}>
        <Button
          key="changeStatus"
          type="text"
          icon={isDeactivated ? <CheckCircleOutlined /> : <StopOutlined />}
          loading={changeUserStatus.loading}
          title={`${isDeactivated ? 'Activate' : 'Deactivate'} user`}
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();

            open({
              icon: <ExclamationCircleFilled />,
              title: `${isDeactivated ? 'Activate' : 'Deactivate'} user?`,
              content: (
                <span>
                  Are you sure you want to
                  {' '}
                  {isDeactivated ? 'Activate' : 'Deactivate'}
                  {' '}
                  user
                  {' '}
                  <b>{record.email}</b>
                  ?
                </span>
              ),
              cancelText: 'Cancel',
              okText: isDeactivated ? 'Activate' : 'Deactivate',
              okButtonProps: {
                danger: true,
              },
              onOk: () => (isDeactivated ? activateUser() : deactivateUser()),
            });
          }}
          {...buttonProps}
        />
      </Tooltip>
      {contextHolder}
    </>
  );
}

export function ResetPasswordButton({ record, buttonProps }: ActionButtonProps) {
  const userForgotPassword = useUserForgotPassword();
  const { open, contextHolder } = useSimpleModal();

  useMessageError([userForgotPassword]);
  useMessageSuccess([userForgotPassword], 'Password reset performed successfully');

  if (!record.email) {
    return null;
  }

  return (
    <>
      <Tooltip title="Reset password" placement="topRight">
        <Button
          key="changeStatus"
          type="text"
          icon={<RedoOutlined />}
          loading={userForgotPassword.loading}
          title="Reset Password"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();

            open({
              icon: <ExclamationCircleFilled />,
              title: 'Reset password?',
              content: (
                <span>
                  Are you sure you want to reset password for user
                  {' '}
                  <b>{record.email}</b>
                  ?
                </span>
              ),
              cancelText: 'Cancel',
              okText: 'Reset',
              okButtonProps: {
                danger: true,
              },
              onOk: () => userForgotPassword.fetch({ email: record?.email || '' }),
            });
          }}
          {...buttonProps}
        />
      </Tooltip>
      {contextHolder}
    </>
  );
}
